import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "@styles/CommonEditorPageLayout.module.css";
import GalleryForm, { GallerySectionWithError } from "@components/forms/GalleryForm/GalleryForm";
import UnsavedChangesPrompt from "@components/misc/UnsavedChangesPrompt";
import EditorCompleteButtons from "@components/buttons/EditorCompleteButtons/EditorCompleteButtons";
import { fetchMemorial, updateMemorialGallery } from "@services/memorialService";
import {
    GalleryMediaAdd,
    GalleryMediaDelete,
    GallerySection,
    GallerySectionAdd,
    GallerySectionChange,
    GalleryUpdate,
} from "@models/Gallery";
import LoadSaveErrorDisplay from "@components/display/LoadSaveErrorDisplay/LoadSaveErrorDisplay";
import { AxiosProgressEvent } from "axios";
import { useAuth } from "@context/auth-context";
import { Memorial } from "@models/Memorial";
import { getRootDomain } from "@util/domain";

const GalleryEditorPage = () => {
    const [searchParams] = useSearchParams();
    const id = searchParams.get("id");

    const { authState } = useAuth();
    const { user } = authState;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [galleryChanges, setGalleryChanges] = useState<GalleryUpdate>({
        sectionAdded: [],
        sectionUpdated: [],
        sectionDeleted: [],
        mediaAdded: [],
        mediaDeleted: [],
    });
    const [iframeKey, setIframeKey] = useState(Date.now());
    const [qrKey, setQrKey] = useState("");
    const [memorial, setMemorial] = useState<Memorial | null>(null);

    const [gallery, setGallery] = useState<GallerySectionWithError[]>([]);
    const [submitErrorMessage, setSubmitErrorMessage] = useState("");

    const hasUnsavedChanges = (galleryChanges.sectionAdded.length > 0)
        || (galleryChanges.sectionUpdated.length > 0) || (galleryChanges.sectionDeleted.length > 0)
        || (galleryChanges.mediaAdded.length > 0) || (galleryChanges.mediaDeleted.length > 0);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                if (!id) {
                    throw new Error("No ID.");
                }
                const data = await fetchMemorial(id);
                setMemorial(data);
                setGallery(data.gallery.map((elem) => ({
                    ...elem,
                    media: elem.media.map((item) => ({
                        _id: item._id,
                        sectionId: elem.id,
                        media: item.src,
                    })),
                    hasError: false,
                    mediaError: "",
                })));
                setQrKey(data.qrKey);
            } catch (err) {
                setError("Failed to fetch memorial information.");
            } finally {
                setLoading(false);
            }
        };
        void fetchData();
    }, [id]);

    const validateGallery = (val: GallerySectionWithError[]) => {
        let err = "";
        val.forEach((entry) => {
            if (entry.hasError || entry.mediaError) {
                err = "Gallery error.";
            }
        });
        return err;
    };

    const onGalleryChange = useCallback((updatedSections: GallerySectionWithError[]) => {
        setGallery([...updatedSections]);
    }, []);

    const onGallerySectionAdd = useCallback((newSection: GallerySectionAdd) => {
        setGalleryChanges((prev) => ({
            ...prev,
            sectionAdded: [...prev.sectionAdded, newSection],
        }));
    }, []);

    const onGallerySectionUpdate = useCallback((updatedSection: GallerySectionChange) => {
        setGalleryChanges((prev) => {
            const isInAdded = prev.sectionAdded.some(
                (section) => section.id === updatedSection.id,
            );

            if (isInAdded) {
                // Merge into sectionAdded
                return {
                    ...prev,
                    sectionAdded: prev.sectionAdded.map((section) => (section.id === updatedSection.id
                        ? { ...section, ...updatedSection }
                        : section)),
                };
            }

            // Merge into sectionUpdated
            const existing = prev.sectionUpdated.find(
                (section) => section.id === updatedSection.id,
            );

            const merged = existing
                ? { ...existing, ...updatedSection }
                : updatedSection;

            return {
                ...prev,
                sectionUpdated: [
                    ...prev.sectionUpdated.filter(
                        (section) => section.id !== updatedSection.id,
                    ),
                    merged,
                ],
            };
        });
    }, []);

    const onGallerySectionDelete = useCallback((sectionId: string) => {
        setGalleryChanges((prev) => {
            // Check if the section is in sectionAdded or sectionUpdated
            const alreadyAddedOrUpdated = prev.sectionAdded.some((section) => section.id === sectionId)
                                          || prev.sectionUpdated.some((section) => section.id === sectionId);

            return {
                ...prev,
                // Add to sectionDeleted only if it's not in sectionAdded or sectionUpdated
                sectionDeleted: alreadyAddedOrUpdated
                    ? prev.sectionDeleted
                    : [...prev.sectionDeleted, sectionId],

                // Remove from other fields if exists
                sectionAdded: prev.sectionAdded.filter((section) => section.id !== sectionId),
                sectionUpdated: prev.sectionUpdated.filter((section) => section.id !== sectionId),
                mediaAdded: prev.mediaAdded.filter((media) => media.sectionId !== sectionId),
                mediaDeleted: prev.mediaDeleted.filter((media) => media.sectionId !== sectionId),
            };
        });
    }, []);

    const onGalleryMediaAdd = useCallback((newMedia: GalleryMediaAdd) => {
        setGalleryChanges((prev) => ({
            ...prev,
            mediaAdded: [...prev.mediaAdded, newMedia],
        }));
    }, []);

    const onGalleryMediaDelete = useCallback((deletedMedia: GalleryMediaDelete) => {
        setGalleryChanges((prev) => {
            // Check if the media is in mediaAdded
            const isMediaAdded = prev.mediaAdded.some((media) => media._id === deletedMedia.mediaId);

            return {
                ...prev,
                // Remove from mediaAdded if it exists
                mediaAdded: prev.mediaAdded.filter((media) => media._id !== deletedMedia.mediaId),

                // Add to mediaDeleted only if it's not in mediaAdded
                mediaDeleted: isMediaAdded
                    ? prev.mediaDeleted
                    : [...prev.mediaDeleted, deletedMedia],
            };
        });
    }, []);

    // Save handler
    const handleSave = async () => {
        let isValid = true;

        const galleryValidationError = validateGallery(gallery);
        if (galleryValidationError) isValid = false;

        if (isValid) {
            try {
                setIsSaving(true);
                setUploadProgress(0);
                setSubmitErrorMessage("");
                if (!id) {
                    throw new Error("No ID.");
                }
                if (hasUnsavedChanges) {
                    const { mediaAdded, ...structuralChanges } = galleryChanges;

                    // Media for newly-added sections must be sent in the same request as those
                    // sections (the backend resolves sectionId via a within-request index lookup).
                    // Media for existing sections can be uploaded one call at a time.
                    const newSectionIds = new Set(
                        galleryChanges.sectionAdded
                            .map((s) => s.id)
                            .filter((sid): sid is string => sid !== undefined),
                    );
                    const mediaForNewSections = mediaAdded.filter((m) => newSectionIds.has(m.sectionId));
                    const mediaForExistingSections = mediaAdded.filter((m) => !newSectionIds.has(m.sectionId));

                    const hasStructuralOrNewSectionChanges =
                        structuralChanges.sectionAdded.length > 0 ||
                        structuralChanges.sectionUpdated.length > 0 ||
                        structuralChanges.sectionDeleted.length > 0 ||
                        structuralChanges.mediaDeleted.length > 0 ||
                        mediaForNewSections.length > 0;

                    const totalCalls =
                        (hasStructuralOrNewSectionChanges ? 1 : 0) + mediaForExistingSections.length;
                    let completedCalls = 0;
                    let updatedGallery: GallerySection[] | undefined;

                    const makeProgressCallback =
                        (callsBefore: number) => (progressEvent: AxiosProgressEvent) => {
                            const fileProgress = progressEvent.loaded / (progressEvent.total || 1);
                            const overall = Math.round(
                                ((callsBefore + fileProgress) / totalCalls) * 100,
                            );
                            setUploadProgress(overall);
                        };

                    // Step 1: structural changes + media for newly-added sections (must be bundled)
                    if (hasStructuralOrNewSectionChanges) {
                        updatedGallery = await updateMemorialGallery(
                            id,
                            { ...structuralChanges, mediaAdded: mediaForNewSections },
                            makeProgressCallback(0),
                        );
                        completedCalls++;
                        setUploadProgress(Math.round((completedCalls / totalCalls) * 100));
                    }

                    // Step 2: one API call per media item for existing sections
                    for (const mediaItem of mediaForExistingSections) {
                        updatedGallery = await updateMemorialGallery(
                            id,
                            {
                                sectionAdded: [],
                                sectionUpdated: [],
                                sectionDeleted: [],
                                mediaAdded: [mediaItem],
                                mediaDeleted: [],
                            },
                            makeProgressCallback(completedCalls),
                        );
                        completedCalls++;
                        setUploadProgress(Math.round((completedCalls / totalCalls) * 100));
                    }

                    if (updatedGallery) {
                        setGallery(
                            updatedGallery.map((elem) => ({
                                ...elem,
                                media: elem.media.map((item) => ({
                                    _id: item._id,
                                    sectionId: elem.id,
                                    media: item.src,
                                })),
                                hasError: false,
                                mediaError: "",
                            })),
                        );
                    }
                }
                setGalleryChanges({
                    sectionAdded: [],
                    sectionUpdated: [],
                    sectionDeleted: [],
                    mediaAdded: [],
                    mediaDeleted: [],
                });
                setIframeKey(Date.now()); // Force preview refresh after save
            } catch (err) {
                const msg = "Failed to save memorial information. Please try again.";
                setSubmitErrorMessage(msg);
                alert(msg);
            } finally {
                setIsSaving(false);
            }
        }
    };

    const baseMemorialUrl = memorial?.iframeUrl ? `${memorial.iframeUrl}?memorialId=${memorial.id}` : `${getRootDomain(memorial?.subdomain)}/memorials/${user?.userType === "demo" ? "example" : (memorial?.urlSlug || memorial?.id)}`;

    const renderContent = () => {
        if (loading || isSaving || error) {
            return (
                <LoadSaveErrorDisplay
                    isLoading={loading}
                    isSaving={isSaving}
                    progress={uploadProgress}
                    showVideoMessage
                    error={error}
                />
            );
        }

        return (
            <div className={styles.container}>
                <div className={styles.headerContainer}>
                    <h1>Gallery Editor</h1>
                </div>
                <div className={styles.buttonGroup}>
                    <EditorCompleteButtons
                        hasUnsavedChanges={hasUnsavedChanges}
                        isSaving={isSaving}
                        onSave={handleSave}
                    />
                </div>
                <div className={styles.editorLayout}>
                    <div className={styles.formSection}>
                        <div className={styles.contentContainer}>
                            <p className={styles.freeVersionNoteMobile}>
                                View this page on a desktop or laptop to see a live preview of your memorial alongside the editor.
                            </p>
                            <GalleryForm
                                state={gallery}
                                onChange={onGalleryChange}
                                onSectionAdd={onGallerySectionAdd}
                                onSectionChange={onGallerySectionUpdate}
                                onSectionDelete={onGallerySectionDelete}
                                onMediaAdd={onGalleryMediaAdd}
                                onMediaDelete={onGalleryMediaDelete}
                            />
                        </div>
                    </div>

                    <div className={styles.previewSection}>
                        <h2>Preview</h2>
                        <div className={styles.previewContainer}>
                            <iframe
                                key={iframeKey}
                                title="Preview Frame"
                                src={`${baseMemorialUrl}#gallery`}
                                className={styles.previewFrame}
                            />
                            {hasUnsavedChanges && (
                                <div className={styles.previewOverlay}>
                                    <p>Save changes to refresh the preview</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {submitErrorMessage && (
                    <p className={styles.errorMessage}>{submitErrorMessage}</p>
                )}
                <UnsavedChangesPrompt hasUnsavedChanges={hasUnsavedChanges} />
            </div>
        );
    };

    return renderContent();
};

export default GalleryEditorPage;
